import { COMPANY } from "./company";

const E = COMPANY.email;

export const ABOUT_PAGE = {
  path: "/about",
  title: "About WanderlyMe — Who we are & how we plan",
  description:
    "WanderlyMe is built by Gurman Soni in Sydney, Australia. Learn why it exists and how every AI-generated itinerary is verified against live Google Places data before you see it.",
  h1: "About WanderlyMe",
  intro:
    "An independent travel planner built in Sydney, designed to make a great trip feel effortless — without pretending the AI is always right.",
  sections: [
    {
      heading: "Who is behind WanderlyMe",
      paragraphs: [
        `WanderlyMe is built and run by ${COMPANY.operator}, a sole trader based in ${COMPANY.location}. There is no large team behind it — just one person who loves travelling, has spent too many evenings buried in browser tabs, and wanted a better way to turn a rough budget into a real plan.`,
        `The business trades as ${COMPANY.tradingName} (ABN ${COMPANY.abn}). You can reach Gurman directly at ${E}.`,
      ],
    },
    {
      heading: "Why it was built",
      paragraphs: [
        "Most trip planning starts the same way: a destination you're excited about, a budget you'd like to stick to, and forty tabs of blog posts, map pins and review sites that never quite agree with each other. The good stuff — the neighbourhood bakery, the viewpoint locals actually use — gets lost somewhere between the top-ten lists.",
        "WanderlyMe was built to compress that work into minutes. You tell it a budget and a place (or ask it to surprise you), and it assembles places to visit, restaurants, hidden gems and a day-by-day itinerary that fits how you like to travel. You can then edit, share, vote with friends, and save it for later.",
      ],
    },
    {
      heading: "How we build our recommendations",
      paragraphs: [
        "We want to be honest about what happens behind the scenes, because it affects how you should use the results.",
      ],
      bullets: [
        "Itineraries are AI-generated. Your inputs (destination, dates, budget, group size and interests) are sent to a third-party AI model, which drafts the plan, suggests venues and explains why each fits your brief.",
        "Every place is then verified against live Google Places data. We check that each attraction, restaurant, hotel and hidden gem actually exists, confirm its location and coordinates, and pull its current opening hours, rating and status.",
        "Anything we can't confirm is labelled. If a suggested venue can't be matched with confidence, we either swap in a real nearby alternative (marked \"Adjusted\") or flag it as \"Unverified\" so you know to double-check before relying on it.",
        "Dates are mapped to real weekdays. When you set travel dates, we re-check opening hours for the actual day you'll be there and warn you about closures.",
        "Routes and travel times come from mapping providers, and budget figures are estimates informed by the AI and public data — they are not quotes.",
      ],
    },
    {
      heading: "Please confirm before you book",
      paragraphs: [
        "WanderlyMe is a planning tool, not a booking service, travel agent or tour operator. AI-generated content can contain mistakes, and venue details change without notice. Travellers should always confirm prices, opening hours, availability, visa requirements and local safety information directly with the venue or an official source before booking or travelling.",
      ],
    },
    {
      heading: "Get in touch",
      paragraphs: [
        `Feedback, corrections and ideas are genuinely welcome — they shape what gets built next. Email ${E} or use the contact page.`,
      ],
    },
  ],
};

export const CONTACT_PAGE = {
  path: "/contact",
  title: "Contact WanderlyMe",
  description:
    "Questions, feedback or a correction for a guide? Email hello@wanderlyme.com or send a message through the WanderlyMe contact form. Based in Sydney, Australia.",
  h1: "Contact us",
  intro:
    "We read every message. Whether it's a bug, a venue that's closed, a refund request or just an idea, this is the place.",
  sections: [
    {
      heading: "Email",
      paragraphs: [
        `${E} — the fastest way to reach us. For privacy requests (access, correction or deletion of your data) please use this address and include the email you registered with.`,
        `${COMPANY.tradingName} is operated by ${COMPANY.operator}, sole trader, ${COMPANY.location}. ABN ${COMPANY.abn}.`,
      ],
    },
  ],
};

export const PRIVACY_PAGE = {
  path: "/privacy",
  title: "Privacy Policy",
  description:
    "How WanderlyMe collects, uses, stores and shares your personal information, aligned with the Australian Privacy Act 1988 and the Australian Privacy Principles. Includes third-party processors, overseas processing and how to access or delete your data.",
  h1: "Privacy Policy",
  intro: `This policy explains how ${COMPANY.tradingName} handles your personal information. It is written to align with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).`,
  sections: [
    {
      heading: "1. Who we are",
      paragraphs: [
        `${COMPANY.tradingName} is operated by ${COMPANY.operator}, a sole trader based in ${COMPANY.location} (ABN ${COMPANY.abn}). In this policy, "we", "us" and "our" refer to ${COMPANY.tradingName}. You can contact us about privacy at ${E}.`,
      ],
    },
    {
      heading: "2. What we collect",
      paragraphs: ["We collect only what we need to run the service:"],
      bullets: [
        "Account details: your name (if provided) and email address, plus a securely hashed password. We never store your password in plain text.",
        "Trip inputs: destination or country, travel dates and year, number of days, group size, budget, interests, origin airport or country, seat class, and any free-text instructions you give the planning assistant.",
        "Generated content and edits: the itineraries, budgets, comments, votes and suggestions you or your collaborators create and save.",
        "Contact messages: name, email, subject and message when you use the contact form or email us.",
        "Payment information: if you purchase a paid plan, payments are processed entirely by Stripe. We receive confirmation of payment, the plan purchased, the amount and a Stripe payment reference. WanderlyMe never receives or stores your card number, expiry or security code.",
        "Usage analytics and technical data: pages visited, approximate location derived from IP address, device and browser type, referring pages and interaction events, collected via Google Analytics and our server logs.",
      ],
    },
    {
      heading: "3. Why we collect it",
      bullets: [
        "To generate, save, edit and share your trip plans.",
        "To create and secure your account, and to send account emails (welcome, password reset, saved-trip copies, collaboration notifications).",
        "To verify venues, calculate routes and travel times, and estimate budgets and flight costs.",
        "To process payments and deliver paid plans, and to handle refunds.",
        "To respond to your messages and support requests.",
        "To understand how the service is used so we can fix problems and improve it.",
        "To comply with legal obligations and enforce our Terms of Service.",
      ],
    },
    {
      heading: "4. Internal customer records",
      paragraphs: [
        "To run the service and support you, WanderlyMe keeps internal records that link your account to the trips you create, the enquiries and contact messages you send, any paid-plan orders you place, and a log of the emails we have sent you. The email log holds metadata only — the type of email, the recipient address, the time it was sent and its delivery status (sent, delivered, bounced or failed) as reported by our email provider. We do not store the contents of those emails in the log.",
        "When you ask us to fulfil a paid plan or respond to an enquiry, we may record short internal notes against your customer record (for example, preferences you have told us about, or the status of your request). These notes are used only for that purpose, are visible only to the operator, and are never shared with other users or third parties except where the law requires.",
        "Access to these records is restricted to the operator's own account and is checked on every request on our servers.",
      ],
    },
    {
      heading: "5. Third-party processors",
      paragraphs: [
        "We rely on a small number of service providers to operate WanderlyMe. Each receives only the data necessary for its function:",
      ],
      bullets: [
        "AI model provider (Anthropic Claude, accessed through our infrastructure provider): receives your trip inputs, assistant messages and existing itinerary content to draft and edit plans.",
        "Google Places API: receives venue names and destination coordinates so we can verify places, opening hours, ratings and photos.",
        "Mapbox: receives place coordinates to calculate routes and travel times.",
        "Stripe: processes payments for paid plans and handles all card data under its own privacy policy. Card details never touch WanderlyMe's servers.",
        "Email provider (Resend): delivers transactional emails such as welcome messages, password resets, itinerary copies and collaboration notifications.",
        "Google Analytics: collects aggregated usage analytics.",
        "Cloud hosting and database providers: store your account and trip data.",
      ],
    },
    {
      heading: "6. Trip prompts are sent to a third-party AI provider",
      paragraphs: [
        "To build your itinerary, the details you enter — destination, dates, budget, interests, group size and any instructions you type to the assistant — are transmitted to a third-party AI model provider. We do not send your password, and we avoid sending your email address or name in prompts unless you include them yourself in free-text fields. Please don't enter sensitive information (health conditions, passport numbers, payment details) into trip prompts.",
      ],
    },
    {
      heading: "7. Overseas processing",
      paragraphs: [
        "Some of the providers listed above store or process data outside Australia, including in the United States and the European Union. By using WanderlyMe you acknowledge that your personal information may be disclosed to overseas recipients. We take reasonable steps to ensure those recipients handle information in a manner consistent with the APPs, including relying on their contractual and security commitments.",
      ],
    },
    {
      heading: "8. Data retention",
      bullets: [
        "Account and saved trips: kept while your account is active. If you delete a trip it moves to Trash and is permanently removed when you empty it or delete your account.",
        "Enquiries and contact messages: kept for 24 months after our last contact with you about them, then deleted or de-identified.",
        "Orders for paid plans: kept for 7 years from the date of the order to meet Australian tax and record-keeping obligations; internal notes attached to an order are deleted with it.",
        "Email log (metadata only): kept for 24 months after the last email sent to you, then deleted.",
        "Deleted accounts: personal data is deleted or de-identified within 30 days of a verified deletion request, except where we must retain records for legal, tax or dispute purposes (typically up to 7 years for payment records).",
        "Password-reset tokens and temporary generation jobs: expire automatically within hours.",
        "Analytics data: retained according to Google Analytics settings (currently 14 months).",
      ],
    },
    {
      heading: "9. Cookies and analytics",
      paragraphs: [
        "We use a strictly necessary authentication cookie (and a browser-stored token) to keep you signed in. We use Google Analytics, which sets cookies to distinguish users and measure usage. You can block or delete cookies in your browser settings; the site will still work but you may need to sign in more often. You can also opt out of Google Analytics with the Google Analytics Opt-out Browser Add-on.",
      ],
    },
    {
      heading: "10. Security",
      paragraphs: [
        "We use encrypted connections (HTTPS), hashed passwords, access-controlled databases and time-limited tokens. No system is perfectly secure, so we can't guarantee absolute security, but we will notify affected users and the Office of the Australian Information Commissioner (OAIC) of any eligible data breach as required by law.",
      ],
    },
    {
      heading: "11. Access, correction and deletion",
      paragraphs: [
        `You can request a copy of the personal information we hold about you — including your account details, trips, enquiries, contact messages, orders, the email log and any internal notes — ask us to correct it, or ask us to delete it, by emailing ${E} from the address you registered with. We will respond within 30 days. There is no charge for making a request. If we must refuse part of a request (for example, where tax law requires us to keep an order record), we will tell you which records are affected and why.`,
      ],
    },
    {
      heading: "12. Complaints",
      paragraphs: [
        `If you believe we have mishandled your personal information, please contact us first at ${E} and we will investigate and respond within 30 days. If you are not satisfied with our response you may complain to the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.`,
      ],
    },
    {
      heading: "13. Changes to this policy",
      paragraphs: [
        `We may update this policy as the service or the law changes. The "last updated" date at the top of this page will change when we do, and material changes will be highlighted in the app or by email. Questions about this policy can be sent to ${E}.`,
      ],
    },
  ],
};

export const TERMS_PAGE = {
  path: "/terms",
  title: "Terms of Service",
  description:
    "The terms that govern your use of WanderlyMe, the AI travel planner. WanderlyMe provides planning information only and is not a travel agent or booking provider. Governed by the laws of New South Wales, Australia.",
  h1: "Terms of Service",
  intro: `These terms are an agreement between you and ${COMPANY.operator} trading as ${COMPANY.tradingName}. By creating an account or using the site you agree to them, so please read them carefully.`,
  sections: [
    {
      heading: "1. About these terms",
      paragraphs: [
        `${COMPANY.tradingName} ("WanderlyMe", "we", "us") is operated by ${COMPANY.operator}, sole trader, ${COMPANY.location} (ABN ${COMPANY.abn}). These Terms of Service, together with our Privacy Policy and Refund Policy, govern your access to and use of wanderlyme.com and related services (the "Service"). If you do not agree, please do not use the Service.`,
      ],
    },
    {
      heading: "2. What WanderlyMe is — and is not",
      paragraphs: [
        "WanderlyMe provides travel planning information only. We generate suggested itineraries, places, restaurants, accommodation ideas, budget estimates and related content to help you plan.",
        "WanderlyMe is not a travel agent, tour operator, airline, accommodation provider or booking provider. We do not sell, arrange or hold bookings, tickets, accommodation, transport, insurance or visas. Any booking you make is a contract between you and the relevant supplier, on that supplier's terms. Prices shown are estimates, not quotes or offers.",
      ],
    },
    {
      heading: "3. AI-generated content and your responsibility to verify",
      paragraphs: [
        "Itineraries and recommendations are generated by artificial intelligence, then checked against third-party data sources such as Google Places. Despite this, AI-generated content may contain errors, omissions or outdated information, and venues may change, close or alter their hours and prices without notice.",
        "You must independently verify opening hours, prices, availability, visa and entry requirements, health advice and safety information with official sources before booking or travelling. Items marked \"Unverified\" or \"Adjusted\" in particular should be treated as suggestions to check, not facts. You are responsible for your own travel decisions.",
      ],
    },
    {
      heading: "4. Accounts",
      bullets: [
        "You must be at least 18 years old, or have a parent or guardian's consent, to create an account.",
        "You must provide accurate information and keep your login details secure. You are responsible for activity under your account; tell us promptly at " + E + " if you suspect unauthorised use.",
        "One account per person. You may not share, sell or transfer your account.",
        "You can delete your account at any time by contacting us. We may suspend or close accounts that breach these terms.",
      ],
    },
    {
      heading: "5. Paid plans",
      paragraphs: [
        "Some features may be offered as paid itinerary plans purchased through WanderlyMe. Prices are shown in the currency indicated at checkout. Payments are processed by Stripe. Delivery times, cancellations and refunds for paid plans are set out in our Refund Policy, which forms part of these terms.",
      ],
    },
    {
      heading: "6. Acceptable use",
      paragraphs: ["You agree not to:"],
      bullets: [
        "use the Service for any unlawful purpose or in breach of any applicable law;",
        "scrape, crawl, copy or bulk-download content, or use automated means to access the Service beyond ordinary browsing and search-engine indexing;",
        "attempt to reverse-engineer, probe or circumvent security or rate limits, or interfere with the Service's operation;",
        "upload or share content that is unlawful, defamatory, infringing, hateful or harmful, or that contains malware;",
        "impersonate any person or misrepresent your affiliation;",
        "resell or commercially redistribute generated itineraries as your own product without our written consent;",
        "use the Service to build a competing dataset or to train machine-learning models.",
      ],
    },
    {
      heading: "7. Intellectual property",
      paragraphs: [
        "The WanderlyMe name, logo, design, software, destination guides and other original content are owned by us or our licensors and are protected by copyright and other laws. We grant you a limited, personal, non-exclusive, non-transferable licence to use the Service and to use the itineraries generated for you for your own personal, non-commercial travel planning.",
        "You keep ownership of the content you enter (trip inputs, comments, suggestions). You grant us a licence to store, process, display and share that content as needed to operate the Service — for example, to show a shared trip to the people you share it with. Third-party data (such as Google Places information and map data) remains the property of its providers and is subject to their terms.",
      ],
    },
    {
      heading: "8. Third-party services and links",
      paragraphs: [
        "The Service relies on third-party providers (AI models, Google Places, Mapbox, Stripe, email delivery) and may link to external websites. We are not responsible for the content, availability or practices of third parties. Your use of third-party services is subject to their own terms.",
      ],
    },
    {
      heading: "9. Your rights under the Australian Consumer Law",
      paragraphs: [
        "Our services come with guarantees that cannot be excluded under the Australian Consumer Law (ACL). For major failures with a service, you are entitled to cancel your service contract with us and to a refund for the unused portion, or to compensation for its reduced value. You are also entitled to be compensated for any other reasonably foreseeable loss or damage. If the failure does not amount to a major failure, you are entitled to have problems with the service rectified in a reasonable time and, if this is not done, to cancel your contract and obtain a refund for the unused portion.",
        "Nothing in these terms excludes, restricts or modifies any consumer guarantee, right or remedy conferred by the ACL or any other law that cannot lawfully be excluded, restricted or modified.",
      ],
    },
    {
      heading: "10. Limitation of liability",
      paragraphs: [
        "Subject to clause 9 and to the extent permitted by law: (a) the Service and its content are provided on an \"as is\" and \"as available\" basis for planning purposes, and we do not guarantee that content is accurate, complete or current; (b) we are not liable for any loss or damage arising from your reliance on generated content, from bookings or purchases you make with third parties, or from travel disruptions, closures, price changes, visa refusals, injury, illness or events outside our reasonable control; (c) we are not liable for indirect or consequential loss, or loss of profit, data or opportunity; and (d) our total aggregate liability to you in connection with the Service in any 12-month period is limited to the greater of the amount you paid us in that period and AUD $100.",
        "Where the law permits liability for breach of a non-excludable guarantee to be limited, our liability is limited, at our option, to re-supplying the relevant service or paying the cost of having it re-supplied.",
      ],
    },
    {
      heading: "11. Changes to the Service and these terms",
      paragraphs: [
        "We may change, suspend or discontinue features at any time. We may update these terms; the \"last updated\" date will change and, for material changes, we will give reasonable notice in the app or by email. Continued use after changes take effect means you accept the updated terms.",
      ],
    },
    {
      heading: "12. Termination",
      paragraphs: [
        "You may stop using the Service at any time. We may suspend or terminate your access if you breach these terms, if required by law, or if we discontinue the Service, in which case we will where practicable give you the opportunity to export your saved trips. Clauses that by their nature should survive (including 7, 9, 10 and 13) continue after termination.",
      ],
    },
    {
      heading: "13. Governing law",
      paragraphs: [
        `These terms are governed by the laws of ${COMPANY.governingLaw}. You and we submit to the non-exclusive jurisdiction of the courts of New South Wales and the courts entitled to hear appeals from them. If you are a consumer in another jurisdiction, this does not deprive you of mandatory protections under your local law.`,
      ],
    },
    {
      heading: "14. Contact",
      paragraphs: [`Questions about these terms: ${E}.`],
    },
  ],
};

export const REFUNDS_PAGE = {
  path: "/refunds",
  title: "Refund & Cancellation Policy",
  description:
    "WanderlyMe's refund and cancellation policy for paid plans: 48-hour delivery commitment, full refund if not delivered within 5 business days, and how to request a refund via hello@wanderlyme.com. In addition to your Australian Consumer Law rights.",
  h1: "Refund & Cancellation Policy",
  intro:
    "Plain-English rules for paid itinerary plans purchased through WanderlyMe. We'd rather refund you than have you feel stuck with something that didn't arrive or didn't work.",
  sections: [
    {
      heading: "1. What this policy covers",
      paragraphs: [
        "This policy applies to any paid itinerary plans or paid features purchased through WanderlyMe. The free planner is not covered because no payment is involved. Bookings you make with airlines, hotels, tour operators or other third parties are governed by those suppliers' own cancellation and refund terms — WanderlyMe does not sell or hold those bookings.",
      ],
    },
    {
      heading: "2. Our 48-hour delivery commitment",
      paragraphs: [
        "We aim to deliver every paid plan within 48 hours of confirmed payment. Most plans are ready far sooner. \"Delivered\" means the plan is available in your account and/or has been sent to the email address on your account.",
      ],
    },
    {
      heading: "3. Full refund if we don't deliver within 5 business days",
      paragraphs: [
        "If your paid plan has not been delivered within 5 business days (Sydney time, excluding NSW public holidays) of confirmed payment, you are entitled to a full refund of the amount paid — no questions asked. You don't need to give us a reason; just let us know.",
      ],
    },
    {
      heading: "4. Other refund situations",
      bullets: [
        "Cancelling before delivery: you can cancel and receive a full refund at any time before your plan has been delivered.",
        "Duplicate or mistaken purchases: refunded in full when reported within 14 days.",
        "Plan not as described or materially defective: if what you received doesn't match what was promised (for example, the wrong destination or a plan that fails to load), we will fix it promptly or refund you.",
        "Change of mind after delivery: because plans are personalised digital content created for you, we don't generally refund change-of-mind requests once delivered — but tell us what went wrong and we'll try to make it right.",
      ],
    },
    {
      heading: "5. How to request a refund",
      paragraphs: [
        `Email ${E} from the address on your WanderlyMe account with the subject "Refund request". Include the date of purchase, the plan or destination, and a brief description of the issue. We will acknowledge your request within 2 business days and let you know the outcome within 5 business days.`,
      ],
    },
    {
      heading: "6. How refunds are paid",
      paragraphs: [
        "Approved refunds are returned to the original payment method through Stripe. Once issued, banks and card providers typically take 5–10 business days to show the funds. We do not charge any fee to process a refund.",
      ],
    },
    {
      heading: "7. Your Australian Consumer Law rights",
      paragraphs: [
        "This policy is in addition to, and does not limit, your rights under the Australian Consumer Law. Our services come with guarantees that cannot be excluded, including that they will be provided with due care and skill, be fit for purpose and be delivered within a reasonable time. If a service fails to meet a consumer guarantee you may be entitled to a remedy — such as a refund, replacement or compensation — regardless of the time limits or conditions set out above.",
      ],
    },
    {
      heading: "8. Contact",
      paragraphs: [
        `Questions about this policy: ${E}. ${COMPANY.tradingName} is operated by ${COMPANY.operator}, sole trader, ${COMPANY.location} (ABN ${COMPANY.abn}). This policy is governed by the laws of ${COMPANY.governingLaw}.`,
      ],
    },
  ],
};

export const PLAN_WITH_US_PAGE = {
  path: "/plan-with-us",
  title: "Plan With Us — Human-Verified Trip Plans in 48 Hours",
  description:
    "Have a real person check every hotel, restaurant, opening hour and price in your WanderlyMe itinerary. Human-verified trip plans delivered within 48 hours, with a full refund if not delivered in 5 business days.",
  h1: "Plan this with a human",
  intro:
    "Our AI builds your trip in seconds. For the trips that really matter, a human can go through every line of it before you book.",
  sections: [
    {
      heading: "What a human-verified plan includes",
      paragraphs: [
        "You keep everything the AI planner gives you — then Gurman personally reviews and tightens it:",
      ],
      bullets: [
        "Every hotel, restaurant, attraction and hidden gem checked by hand against current listings, not just automated Google Places data.",
        "Opening hours and closures confirmed for the exact weekdays you'll be there, including public holidays and seasonal breaks.",
        "Prices and booking links re-checked, with suggestions where something cheaper or better-located exists for your budget.",
        "Realistic timings — travel legs, queue times and meal windows sanity-checked so each day actually fits.",
        "Visa, entry and safety notes for your nationality and dates, with links to the official sources.",
        "A short written summary of what we changed and why, plus the final itinerary delivered to your inbox and saved to your account.",
      ],
    },
    {
      heading: "48-hour turnaround",
      paragraphs: [
        "Send us your trip details using the form below (or from the bottom of any generated itinerary). We'll acknowledge it straight away and deliver your verified plan within 48 hours of confirmed payment. Most plans arrive sooner.",
        "If we don't deliver within 5 business days you are entitled to a full refund — no questions asked. Our full Refund & Cancellation Policy is at wanderlyme.com/refunds, and it sits alongside your rights under the Australian Consumer Law.",
      ],
    },
    {
      heading: "Still your plan, still your bookings",
      paragraphs: [
        "WanderlyMe provides planning information only. We don't sell or hold bookings, tickets or accommodation — you book directly with each provider on their terms, which keeps you in control and keeps our advice independent. Even after a human review, please double-check anything time-critical with the venue before you commit.",
      ],
    },
  ],
};

export const LEGAL_PAGES = [ABOUT_PAGE, CONTACT_PAGE, PRIVACY_PAGE, TERMS_PAGE, REFUNDS_PAGE];
